import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import api from '../services/api';
import ReactMarkdown from 'react-markdown';

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(`/blog/${slug}`);
        setPost(response.data.post);
        setError(null);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Failed to fetch blog post. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      fetchPost();
    }
  }, [slug]);

  if (isLoading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading blog post...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="text-red-500 text-6xl mb-4">📄</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Post Not Found</h2>
              <p className="text-gray-600 mb-6">{error || 'The blog post you are looking for does not exist.'}</p>
              <button
                onClick={() => navigate('/blog')}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
              >
                ← Back to Blog
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
            {/* Back Button */}
            <button
              onClick={() => navigate('/blog')}
              className="flex items-center text-primary-600 hover:text-primary-700 mb-6 transition"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Blog
            </button>

            {/* Article Header */}
            <article className="bg-white rounded-lg shadow-lg overflow-hidden">
              {/* Cover Image */}
              {post.cover_image_url && (
                <div className="w-full h-96 overflow-hidden">
                  <img
                    src={post.cover_image_url}
                    alt={post.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Content */}
              <div className="p-8 lg:p-12">
                {/* Category */}
                {post.category && (
                  <div className="mb-4">
                    <span className="inline-block bg-primary-100 text-primary-600 px-4 py-2 rounded-full text-sm font-medium">
                      {post.category}
                    </span>
                  </div>
                )}

                {/* Title */}
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  {post.title}
                </h1>

                {/* Meta Information */}
                <div className="flex flex-wrap items-center gap-6 pb-6 mb-8 border-b border-gray-200">
                  <div className="flex items-center text-gray-600">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{post.author_name || 'Anonymous'}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>{new Date(post.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  {post.views && (
                    <div className="flex items-center text-gray-600">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span>{post.views} views</span>
                    </div>
                  )}
                </div>

                {/* Excerpt */}
                {post.excerpt && (
                  <div className="bg-gray-50 border-l-4 border-primary-500 p-6 mb-8 rounded-r-lg">
                    <p className="text-lg text-gray-700 italic leading-relaxed">
                      {post.excerpt}
                    </p>
                  </div>
                )}

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-8">
                    {post.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-block bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Blog Content with Markdown */}
                <div className="prose prose-lg max-w-none">
                  <ReactMarkdown
                    components={{
                      // Custom image rendering with better styling
                      img: ({node, ...props}) => (
                        <div className="my-8">
                          <img
                            {...props}
                            className="w-full rounded-lg shadow-md"
                            loading="lazy"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/800x400?text=Image+Not+Available';
                            }}
                          />
                          {props.alt && props.alt !== 'image' && (
                            <p className="text-center text-sm text-gray-500 mt-2 italic">{props.alt}</p>
                          )}
                        </div>
                      ),
                      // Custom heading rendering
                      h1: ({node, ...props}) => (
                        <h1 className="text-3xl font-bold mt-12 mb-6 text-gray-900" {...props} />
                      ),
                      h2: ({node, ...props}) => (
                        <h2 className="text-2xl font-bold mt-10 mb-5 text-gray-900" {...props} />
                      ),
                      h3: ({node, ...props}) => (
                        <h3 className="text-xl font-semibold mt-8 mb-4 text-gray-900" {...props} />
                      ),
                      // Custom paragraph rendering
                      p: ({node, ...props}) => (
                        <p className="mb-6 text-gray-700 leading-relaxed" {...props} />
                      ),
                      // Custom link rendering
                      a: ({node, ...props}) => (
                        <a className="text-primary-600 hover:text-primary-700 underline" target="_blank" rel="noopener noreferrer" {...props} />
                      ),
                      // Custom list rendering
                      ul: ({node, ...props}) => (
                        <ul className="mb-6 space-y-2 list-disc list-inside" {...props} />
                      ),
                      ol: ({node, ...props}) => (
                        <ol className="mb-6 space-y-2 list-decimal list-inside" {...props} />
                      ),
                      // Custom code rendering
                      code: ({node, inline, ...props}) => (
                        inline
                          ? <code className="bg-gray-100 text-red-600 px-2 py-1 rounded text-sm font-mono" {...props} />
                          : <code className="block bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono my-6" {...props} />
                      ),
                      // Custom blockquote rendering
                      blockquote: ({node, ...props}) => (
                        <blockquote className="border-l-4 border-primary-500 pl-6 py-4 my-6 bg-gray-50 rounded-r-lg italic text-gray-700" {...props} />
                      ),
                    }}
                  >
                    {post.content}
                  </ReactMarkdown>
                </div>
              </div>
            </article>

            {/* Back to Blog Button */}
            <div className="mt-12 text-center">
              <button
                onClick={() => navigate('/blog')}
                className="px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition shadow-md hover:shadow-lg"
              >
                ← Back to All Posts
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
